import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, UserRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import ListFilterBar from '../components/work/ListFilterBar';
import { PageFrame } from '../components/work/page-chrome';
import { BrandEmpty, ErrorAlert, ListEmpty, LoadingRows, PageTitle } from '../components/work/page-states';
import StarProjectButton from '../components/work/StarProjectButton';
import { titleLinkClass } from '../components/work/table-cells';
import { useDirectoryQuery } from '../components/work/use-directory-query';
import { useSession } from '../components/session-context';
import { useOverlays } from '../components/work/overlay-context';
import { apiErrorMessage, apiFetch, v1Path } from '../lib/api';
import { projectInitial } from '../lib/assigned-queue';
import { projectWorkPath } from '../lib/project-path';
import {
  filterProjects,
  PROJECT_ACCESS,
  PROJECT_ACCESS_LABELS,
  PROJECT_DEFAULT_ORDER,
  PROJECT_SORT_OPTIONS,
  projectAccess,
  projectAccessLabel,
} from '../lib/project-list';
import { isWorkspaceProject } from '../lib/workspace-projects';
import type { ProjectDto } from '../lib/work-types';
import { htmlToPlainText } from '../lib/rich-text';

const PROJECT_CHIPS = ['access'] as const;

function ProjectName({ project }: { project: ProjectDto }) {
  return (
    <span className="flex min-w-0 items-center gap-2.5">
      <span
        aria-hidden
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-xs font-semibold text-muted-foreground"
      >
        {projectInitial(project.name, project.identifier)}
      </span>
      <span className="min-w-0">
        <span className="flex items-baseline gap-2">
          <span className={`truncate ${titleLinkClass}`}>{project.name}</span>
          <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">
            {project.identifier}
          </span>
        </span>
      </span>
    </span>
  );
}

function RequestAction({
  requested,
  requesting,
  onRequest,
}: {
  requested: boolean;
  requesting: boolean;
  onRequest: () => void;
}) {
  if (requested) {
    return <span className="text-xs text-muted-foreground">Requested</span>;
  }
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="border-primary/40 font-medium text-primary hover:bg-primary/10 hover:text-primary"
      disabled={requesting}
      onClick={onRequest}
    >
      {requesting ? 'Requesting…' : 'Request access'}
    </Button>
  );
}

export default function Projects() {
  const { ready, isAdmin, isOrgAdmin } = useSession();
  const { openCreateProject } = useOverlays();
  const navigate = useNavigate();
  const filters = useDirectoryQuery(PROJECT_CHIPS, PROJECT_DEFAULT_ORDER);
  const [items, setItems] = useState<ProjectDto[]>([]);
  const [requestable, setRequestable] = useState<ProjectDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requestingId, setRequestingId] = useState<string | null>(null);
  const [joiningId, setJoiningId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const mine = await apiFetch<{ items: ProjectDto[] }>(v1Path('/projects?limit=100&offset=0'));
      const streams = mine.items.filter((project) => !isWorkspaceProject(project));
      if (isOrgAdmin) {
        setItems(streams);
        setRequestable([]);
        return;
      }
      const ask = await apiFetch<{ items: ProjectDto[] }>(v1Path('/projects/requestable'));
      const requestedIds = new Set(
        ask.items.filter((project) => project.requested === true).map((project) => project.id),
      );
      setItems(streams.map((project) => (
        project.is_member === false && requestedIds.has(project.id)
          ? { ...project, requested: true }
          : project
      )));
      setRequestable(ask.items.filter((project) => !isWorkspaceProject(project)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load streams');
      setItems([]);
      setRequestable([]);
    } finally {
      setLoading(false);
    }
  }, [isOrgAdmin]);

  useEffect(() => {
    if (!ready) return;
    void load();
  }, [ready, load]);

  const listedIds = useMemo(() => new Set(items.map((project) => project.id)), [items]);
  const extraRequestable = useMemo(
    () => requestable.filter((project) => !listedIds.has(project.id)),
    [requestable, listedIds],
  );
  const visibleItems = useMemo(
    () =>
      filterProjects(items, {
        query: filters.query,
        access: filters.selected.access,
        orderBy: filters.orderBy,
      }),
    [filters.orderBy, filters.query, filters.selected.access, items],
  );
  const visibleExtra = useMemo(
    () =>
      filterProjects(extraRequestable, {
        query: filters.query,
        access: filters.selected.access,
        orderBy: filters.orderBy,
      }),
    [extraRequestable, filters.orderBy, filters.query, filters.selected.access],
  );

  async function requestAccess(projectId: string): Promise<void> {
    setRequestingId(projectId);
    setError(null);
    try {
      await apiFetch(v1Path(`/projects/${projectId}/access-requests`), { method: 'POST' });
      await load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to request access'));
    } finally {
      setRequestingId(null);
    }
  }

  async function joinProject(project: ProjectDto): Promise<void> {
    setJoiningId(project.id);
    setError(null);
    try {
      await apiFetch(v1Path(`/projects/${project.id}/join`), { method: 'POST' });
      navigate(projectWorkPath(project.id));
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to join stream'));
    } finally {
      setJoiningId(null);
    }
  }

  const empty = items.length === 0 && extraRequestable.length === 0;
  const noMatches = !empty && visibleItems.length === 0 && visibleExtra.length === 0;

  return (
    <PageFrame>
      <div className="flex flex-1 flex-col gap-4">
        <PageTitle
          title="Streams"
          count={loading || error ? undefined : visibleItems.length + visibleExtra.length}
          action={
            isAdmin ? (
              <Button type="button" size="sm" onClick={openCreateProject}>
                <Plus className="h-4 w-4" aria-hidden />
                New stream
              </Button>
            ) : null
          }
        />

        <ListFilterBar
          query={filters.query}
          onQueryChange={(query) => filters.apply({ query })}
          searchPlaceholder="Search name or key..."
          searchAriaLabel="Search streams"
          chips={[
            {
              id: 'access',
              label: 'Access',
              icon: UserRound,
              options: PROJECT_ACCESS.map((value) => ({ value, label: PROJECT_ACCESS_LABELS[value] })),
              selected: filters.selected.access,
              onToggle: (value) => filters.toggleChip('access', value),
            },
          ]}
          sort={{
            value: filters.orderBy ?? PROJECT_DEFAULT_ORDER,
            options: [...PROJECT_SORT_OPTIONS],
            onChange: (value) => filters.apply({ orderBy: value }),
          }}
          onClear={filters.clear}
        />

        {error ? <ErrorAlert>{error}</ErrorAlert> : null}
        {loading ? (
          <LoadingRows columns={4} />
        ) : empty ? (
          <BrandEmpty title="No streams yet." />
        ) : noMatches ? (
          <ListEmpty
            title="No streams match"
            description="Clear filters if you expected matches."
            action={
              <Button type="button" variant="outline" size="sm" onClick={filters.clear}>
                Clear filters
              </Button>
            }
          />
        ) : (
          <div className="space-y-8">
            {visibleItems.length > 0 ? (
              <ProjectTable
                items={visibleItems}
                isOrgAdmin={isOrgAdmin}
                joiningId={joiningId}
                requestingId={requestingId}
                onJoin={(project) => void joinProject(project)}
                onRequest={(id) => void requestAccess(id)}
              />
            ) : (
              <ListEmpty title="No streams yet." />
            )}
            {visibleExtra.length > 0 ? (
              <section>
                <h2 className="mb-2 text-sm font-medium">Ask for access</h2>
                <ProjectTable
                  items={visibleExtra}
                  isOrgAdmin={false}
                  joiningId={null}
                  requestingId={requestingId}
                  requestOnly
                  onJoin={() => undefined}
                  onRequest={(id) => void requestAccess(id)}
                />
              </section>
            ) : null}
          </div>
        )}
      </div>
    </PageFrame>
  );
}

function ProjectTable({
  items,
  isOrgAdmin,
  joiningId,
  requestingId,
  requestOnly = false,
  onJoin,
  onRequest,
}: {
  items: ProjectDto[];
  isOrgAdmin: boolean;
  joiningId: string | null;
  requestingId: string | null;
  requestOnly?: boolean;
  onJoin: (project: ProjectDto) => void;
  onRequest: (projectId: string) => void;
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border/80 bg-card shadow-[0_1px_2px_hsl(var(--brand-navy)/0.04),0_4px_12px_-6px_hsl(var(--brand-navy)/0.08)]">
    <Table className="table-fixed">
      <TableHeader>
        <TableRow>
          <TableHead className="min-w-0 w-[32%]">Stream</TableHead>
          <TableHead className="hidden min-w-0 md:table-cell">Description</TableHead>
          <TableHead className="hidden w-32 sm:table-cell">Access</TableHead>
          <TableHead className="w-40 text-right">
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((project) => {
          const access = projectAccess(project);
          const showRequest = !isOrgAdmin && (requestOnly || project.is_member === false);
          const canStar = !requestOnly && (isOrgAdmin || project.is_member !== false);
          const description = htmlToPlainText(project.description ?? '');
          return (
            <TableRow key={project.id}>
              <TableCell className="min-w-0">
                {requestOnly ? (
                  <ProjectName project={project} />
                ) : (
                  <Link
                    to={projectWorkPath(project.id)}
                    className="block min-w-0"
                    onClick={(event) => {
                      if (!isOrgAdmin || project.is_member !== false) return;
                      event.preventDefault();
                      onJoin(project);
                    }}
                  >
                    <ProjectName project={project} />
                  </Link>
                )}
              </TableCell>
              <TableCell className="hidden min-w-0 truncate text-xs text-muted-foreground md:table-cell">
                {description || '—'}
              </TableCell>
              <TableCell className="hidden text-xs text-muted-foreground sm:table-cell">
                {joiningId === project.id ? 'Joining…' : projectAccessLabel(access)}
              </TableCell>
              <TableCell className="text-right">
                <span className="inline-flex items-center justify-end gap-2">
                  {canStar ? <StarProjectButton projectId={project.id} /> : null}
                  {showRequest ? (
                    <RequestAction
                      requested={project.requested === true}
                      requesting={requestingId === project.id}
                      onRequest={() => onRequest(project.id)}
                    />
                  ) : null}
                </span>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
    </div>
  );
}
